import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux'; // Import useSelector
import { selectIsAuthenticated } from '../../features/auth/authSlice'; // Import selectIsAuthenticated
import { useGetUserSavedPropertiesQuery, useDeleteFavoriteMutation } from '../../features/api/apiSlice'; // Import query and mutation
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PropertyCard from '../home/components/PropertyCard'; // Use the shared PropertyCard
import EmptyState from './components/EmptyState';
import SortingControls from './components/SortingControls'; // Import SortingControls
import BulkActionsToolbar from './components/BulkActionsToolbar'; // Import BulkActionsToolbar
import { useToast } from '../../components/ui/Toast'; // Import useToast

const SavedPropertiesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useSelector(selectIsAuthenticated); // Get isAuthenticated from Redux store
  const addToast = useToast(); // Initialize useToast

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('savedDate'); // This will need to be adjusted based on API capabilities
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch saved properties from API
  const { data: apiSavedProperties = [], isLoading, isError, error, refetch } = useGetUserSavedPropertiesQuery(undefined, {
    skip: !isAuthenticated, // Skip fetching if not authenticated
  });

  const [deleteFavorite] = useDeleteFavoriteMutation();

  // Filter properties based on search query (client-side for now)
  const getFilteredProperties = () => {
    let filtered = apiSavedProperties.map(item => ({
      ...item.property, // Assuming 'property' contains the full property details
      savedDate: item.saved_date, // Add saved_date from the saved_property object
      id: item.property_id // Ensure the ID is the property_id for toggling
    }));

    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(property =>
        property?.title?.toLowerCase()?.includes(query) ||
        property?.location?.toLowerCase()?.includes(query) ||
        property?.type?.toLowerCase()?.includes(query)
      );
    }

    // Sort properties (client-side for now)
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'savedDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  };

  const filteredProperties = getFilteredProperties();

  // Handle property selection for bulk actions
  const handlePropertySelect = (propertyId) => {
    setSelectedProperties(prev => {
      const isSelected = prev?.includes(propertyId);
      const newSelection = isSelected 
        ? prev?.filter(id => id !== propertyId)
        : [...prev, propertyId];
      
      setShowBulkActions(newSelection?.length > 0);
      return newSelection;
    });
  };

  // Handle removing a single property from favorites
  const handleRemoveProperty = async (propertyId) => {
    if (window.confirm('Are you sure you want to remove this property from your saved list?')) {
      try {
        await deleteFavorite(propertyId).unwrap();
        addToast('Property removed from saved list.', 'info'); // Show info toast
        setSelectedProperties(prev => prev?.filter(id => id !== propertyId));
        refetch(); // Refetch saved properties to update the list
      } catch (err) {
        console.error('Failed to remove favorite property:', err);
        addToast('Failed to remove property. Please try again.', 'error'); // Use toast for error
      }
    }
  };

  // Handle bulk remove
  const handleBulkRemove = async () => {
    if (window.confirm(`Remove ${selectedProperties?.length} properties from saved list?`)) {
      try {
        await Promise.all(selectedProperties.map(id => deleteFavorite(id).unwrap()));
        addToast(`${selectedProperties.length} properties removed from saved list.`, 'info'); // Show info toast
        setSelectedProperties([]);
        setShowBulkActions(false);
        refetch(); // Refetch saved properties to update the list
      } catch (err) {
        console.error('Failed to bulk remove favorite properties:', err);
        addToast('Failed to bulk remove properties. Please try again.', 'error'); // Use toast for error
      }
    }
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
            <h1 className="text-3xl font-bold text-foreground mb-4">Access Denied</h1>
            <p className="text-muted-foreground mb-6">Please log in to view your saved properties.</p>
            <Button onClick={() => navigate('/login')}>Go to Login</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground flex items-center space-x-3">
                  <Icon name="Heart" size={32} className="text-primary" />
                  <span>Saved Properties</span>
                </h1>
                <p className="text-muted-foreground mt-2">
                  Manage your favorite properties and organize them into collections
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="Search"
                  iconPosition="left"
                  iconSize={16}
                  onClick={() => navigate('/property-search-browse')}
                >
                  Find More Properties
                </Button>
                
                {selectedProperties?.length > 0 && (
                  <Button
                    variant="ghost"
                    iconName="Square"
                    iconPosition="left"
                    iconSize={16}
                    onClick={() => setShowBulkActions(!showBulkActions)}
                  >
                    Select Mode
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Search */}
              <div className="bg-card border border-border rounded-lg p-4">
                <Input
                  type="search"
                  placeholder="Search saved properties..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full"
                />
              </div>

              {/* Folder Manager (Removed for now, as API doesn't support it directly) */}
              {/* <FolderManager
                folders={folders}
                activeFolder={activeFolder}
                onFolderChange={handleFolderChange}
                onCreateFolder={handleCreateFolder}
                onDeleteFolder={handleDeleteFolder}
                onRenameFolder={handleRenameFolder}
                propertyCounts={getPropertyCounts()}
              /> */}
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Sorting Controls */}
              <SortingControls
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                totalCount={filteredProperties?.length}
              />

              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="bg-card border border-border rounded-lg overflow-hidden shadow-sm animate-pulse">
                      <div className="h-48 bg-muted" />
                      <div className="p-4 space-y-3">
                        <div className="h-6 bg-muted rounded w-3/4" />
                        <div className="h-4 bg-muted rounded w-full" />
                        <div className="h-4 bg-muted rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredProperties?.length > 0 ? (
                <div className={`grid gap-6 ${
                  viewMode === 'grid' ?'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' :'grid-cols-1'
                }`}>
                  {filteredProperties?.map((property) => (
                    <PropertyCard
                      key={property?.id}
                      property={property}
                      isFavorite={true} // All properties here are favorites
                      isSelected={selectedProperties?.includes(property?.id)}
                      onSelect={handlePropertySelect}
                      onRemove={handleRemoveProperty} // Use local handler for removal
                      // onMoveToFolder={handleMoveToFolder} // Removed folder logic
                      // folders={folders} // Removed folder logic
                      showCheckbox={showBulkActions}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  type={'all'} // Simplified type
                  folderName={''}
                  onCreateFolder={() => navigate('/property-search-browse')} // Redirect to find properties
                />
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Bulk Actions Toolbar */}
      {showBulkActions && (
        <BulkActionsToolbar
          selectedCount={selectedProperties?.length}
          // onCompare={handleBulkCompare} // Removed comparison for now
          onRemove={handleBulkRemove}
          // onMoveToFolder={handleBulkMoveToFolder} // Removed folder logic
          onClearSelection={() => {
            setSelectedProperties([]);
            setShowBulkActions(false);
          }}
          // folders={folders} // Removed folder logic
        />
      )}
      {/* Property Comparison Modal (Removed for now) */}
      {/* {showComparison && (
        <PropertyComparison
          properties={comparisonProperties}
          onClose={() => setShowComparison(false)}
          onRemoveFromComparison={handleRemoveFromComparison}
        />
      )} */}
    </div>
  );
};

export default SavedPropertiesPage;
